import React, { useState, useEffect, useCallback, useRef } from "react";
import "./VideoPlayer.css";

const defaultPlayIcon = require("../../imgs/play_icon.png");
const defaultCloseIcon = require("../../imgs/close_w.png");
const defaultAlertIcon = require("../../imgs/section_icon.png");

export const VideoPlayer = ({
  videoCover,
  videoURL = "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
  width = "100%",
  maxWidth = "100%",
  height = "auto",
  showPlayButton = true,
  playButtonSize = 50,
  borderRadius = 8,
  onPress,
  autoPlay = true,
  muted = false,
  loop = false,
  inner = true,
}) => {
  // 状态管理
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  // 视频元素引用
  const videoRef = useRef(null);
  // 弹窗容器引用
  const modalRef = useRef(null);

  // 监听视频加载状态
  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;

    const handleLoadStart = () => {
      setIsLoading(true);
      setError(null);
    };

    const handleLoadedData = () => {
      setIsLoading(false);
      setError(null);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      setError(null);
    };

    const handlePlaying = () => {
      setIsPlaying(true);
      setIsLoading(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleStalled = () => {
      setIsLoading(true);
    };

    const handleError = (e) => {
      setIsLoading(false);
      setIsPlaying(false);
      const errorCode = e.target.error?.code;
      let errorMsg = "视频加载出错，请检查URL或网络";

      switch (errorCode) {
        case 1:
          errorMsg = "视频加载中止";
          break;
        case 2:
          errorMsg = "网络错误，请检查网络连接";
          break;
        case 3:
          errorMsg = "视频解码错误";
          break;
        case 4:
          errorMsg = "视频格式不支持";
          break;
      }

      setError({ message: errorMsg });
      console.error("视频播放错误:", e.target.error);
    };

    const handleVisibilityChange = () => {
      if (document.hidden && videoRef.current) {
        videoRef.current.pause();
      }
    };

    // 绑定事件
    video.addEventListener("loadstart", handleLoadStart);
    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("playing", handlePlaying);
    video.addEventListener("pause", handlePause);
    video.addEventListener("stalled", handleStalled);
    video.addEventListener("error", handleError);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // 初始化视频属性
    video.loop = loop;
    video.muted = muted;
    video.preload = "auto";
    video.playsInline = true;

    // 清理函数
    return () => {
      video.removeEventListener("loadstart", handleLoadStart);
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("playing", handlePlaying);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("stalled", handleStalled);
      video.removeEventListener("error", handleError);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loop, muted]);

  // 关闭弹窗时暂停视频
  const handleCloseModal = useCallback(async () => {
    try {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    } catch (e) {
      console.warn("关闭视频失败", e);
    }
    setIsLoading(false);
    setIsPlaying(false);
    setError(null);
    setShowVideoModal(false);
    setHasStarted(false);
  }, []);

  // 播放视频
  const handlePlay = useCallback(async () => {
    onPress?.();
    setShowVideoModal(true);
    setHasStarted(true);
    setError(null);
    setIsLoading(true);

    setTimeout(() => {
      const initVideo = async () => {
        const video = videoRef.current;
        if (!video) return;

        try {
          video.pause();
          video.currentTime = 0;
          video.src = videoURL;
          video.loop = loop;
          video.muted = muted;
          video.playsInline = true;
          video.preload = "auto";
          video.crossOrigin = "anonymous";
          video.load();

          if (autoPlay) {
            try {
              await video.play();
              setIsLoading(false);
              setIsPlaying(true);
            } catch (playError) {
              console.warn("自动播放被阻止:", playError);

              if (playError.name === "NotAllowedError") {
                video.muted = true;
                try {
                  await video.play();
                  setIsLoading(false);
                  setIsPlaying(true);
                } catch (mutedError) {
                  console.error("静音播放也失败:", mutedError);
                  setIsLoading(false);
                  setError({
                    message: "请点击视频上的播放按钮手动播放",
                  });
                }
              } else {
                setIsLoading(false);
                setError({ message: "播放失败，请手动播放" });
              }
            }
          } else {
            setIsLoading(false);
          }
        } catch (e) {
          console.error("初始化视频失败:", e);
          setIsLoading(false);
          setError({ message: "视频初始化失败" });
        }
      };

      initVideo();
    }, 50);
  }, [onPress, videoURL, loop, muted, autoPlay]);

  // 当弹窗打开后初始化视频
  useEffect(() => {
    if (showVideoModal && hasStarted && videoRef.current) {
      const video = videoRef.current;
      video.playsInline = true;
    }
  }, [showVideoModal, hasStarted]);

  // 重试播放
  const handleRetry = useCallback(async () => {
    if (!videoRef.current) return;

    try {
      setError(null);
      setIsLoading(true);

      const video = videoRef.current;
      video.pause();
      video.currentTime = 0;
      video.src = videoURL;
      video.load();

      await video.play();

      setIsLoading(false);
      setIsPlaying(true);
    } catch (e) {
      console.error("重试播放失败:", e);
      setIsLoading(false);

      if (e.name === "NotAllowedError") {
        setError({ message: "请点击视频上的播放按钮手动播放" });
      } else {
        setError({ message: "重试失败，请检查网络连接" });
      }
    }
  }, [videoURL]);

  // 点击弹窗外部关闭
  const handleModalClick = (e) => {
    if (modalRef.current === e.target) {
      handleCloseModal();
    }
  };

  // 处理视频封面加载失败
  const handleCoverError = (e) => {
    e.target.src =
      "https://via.placeholder.com/375x210/000000/FFFFFF?text=视频封面";
  };

  // 计算播放图标尺寸
  const getPlayIconStyle = () => ({
    width: inner ? "60%" : "80%",
    height: inner ? "60%" : "80%",
  });

  // 计算播放按钮样式
  const getPlayButtonStyle = () => ({
    width: `${playButtonSize}px`,
    height: `${playButtonSize}px`,
    fontSize: inner ? `${playButtonSize * 0.4}px` : `${playButtonSize * 0.5}px`,
  });

  return (
    <>
      {/* 视频封面 */}
      <div
        className="video-player-container"
        style={{
          width,
          maxWidth,
          height,
          borderRadius: `${borderRadius}px`,
        }}
        onClick={handlePlay}
      >
        <img
          src={
            videoCover ||
            "https://via.placeholder.com/375x210/000000/FFFFFF?text=视频封面"
          }
          alt="视频封面"
          className="video-cover"
          style={{
            borderRadius: `${borderRadius}px`,
          }}
          onError={handleCoverError}
        />
        <div
          className="video-overlay"
          style={{ borderRadius: `${borderRadius}px` }}
        />

        {/* 播放按钮 */}
        {showPlayButton && (
          <button
            className={`play-button ${
              inner ? "play-button-inner" : "play-button-outer"
            }`}
            style={getPlayButtonStyle()}
            onClick={(e) => {
              e.stopPropagation();
              handlePlay();
            }}
          >
            <img src={defaultPlayIcon} alt="播放" style={getPlayIconStyle()} />
          </button>
        )}

        {/* 视频提示文字 */}
        <div className="video-info">
          <img src={defaultPlayIcon} alt="播放" className="play-icon-sm" />
          <span>播放视频</span>
        </div>
      </div>

      {/* 视频播放弹窗 */}
      {showVideoModal && (
        <div
          className="video-modal-overlay"
          onClick={handleModalClick}
          ref={modalRef}
        >
          {/* 关闭按钮 */}
          <button className="close-button" onClick={handleCloseModal}>
            <img src={defaultCloseIcon} alt="关闭" className="close-icon" />
          </button>

          {/* 视频容器 */}
          <div className="video-player-wrapper">
            {/* 加载状态 */}
            {isLoading && (
              <div className="loading-container">
                <div className="loading-spinner" />
                <span className="loading-text">视频加载中...</span>
              </div>
            )}

            {/* 错误状态 */}
            {error && !isLoading && (
              <div className="error-container">
                <img src={defaultAlertIcon} alt="错误" className="alert-icon" />
                <span className="error-text">{error.message}</span>
                <div className="error-buttons">
                  <button className="retry-button" onClick={handleRetry}>
                    重试播放
                  </button>
                  <button
                    className="close-error-button"
                    onClick={handleCloseModal}
                  >
                    关闭
                  </button>
                </div>
              </div>
            )}

            {/* HTML5 视频播放器 */}
            <video
              ref={videoRef}
              className={`html5-video-player ${
                isLoading || error ? "video-hidden" : ""
              }`}
              controls
              playsInline
              preload="auto"
              crossOrigin="anonymous"
              webkit-playsinline="true"
              x5-playsinline="true"
              x5-video-player-type="h5"
              x5-video-player-fullscreen="true"
              x5-video-orientation="portrait"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default VideoPlayer;
